import { ReactNode } from 'react';
import { Badge, HStack, Tooltip } from '@chakra-ui/react';
import { PublishedStatusBadge } from './PublishedStatusBadge';

type PublishedState = {
  isPublished: boolean;
  publishedAt?: Date | string | null;
  publishedVersion?: number | null;
  /** When published and the draft has moved ahead of the live version. */
  hasUnpublishedChanges?: boolean;
};

/**
 * The publish-state cluster for an editor toolbar's `status` slot: the Published/Draft
 * pill (replaced by "Unpublished changes" when the draft has moved ahead), an optional
 * supersession pill, version history, then any `derivedOutputs` (e.g. translations). It
 * deliberately does NOT own the save-status indicator (Saving/Saved), a transient signal
 * that lives in the toolbar's editing cluster.
 */
export function EditorStatus({
  published,
  superseded,
  versionHistory,
  derivedOutputs,
}: {
  published: PublishedState;
  /** Course supersession. Rendered as a pill in the same family, not the bold
   *  solid `ResourceStatusBadge` used for student progress elsewhere. */
  superseded?: { label: ReactNode; tooltip?: ReactNode };
  /** e.g. a `<FlowVersionHistory />` popover. */
  versionHistory?: ReactNode;
  /** Outputs derived from the published version (translations today), shown as the
   *  last item in the cluster so they read as hanging off what's live. */
  derivedOutputs?: ReactNode;
}) {
  return (
    <HStack spacing="2">
      <PublishedStatusBadge
        isPublished={published.isPublished}
        publishedAt={published.publishedAt}
        publishedVersion={published.publishedVersion}
        hasUnpublishedChanges={published.hasUnpublishedChanges}
      />
      {superseded ? (
        <Tooltip hasArrow label={superseded.tooltip} placement="bottom">
          <Badge
            colorScheme="red"
            variant="pill"
            fontSize="xs"
            fontWeight="semibold"
            cursor="default"
          >
            {superseded.label}
          </Badge>
        </Tooltip>
      ) : null}
      {versionHistory}
      {derivedOutputs}
    </HStack>
  );
}
